import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import LoadingSpinner from '../components/common/LoadingSpinner';
import Notification from '../components/common/Notification';
import ConfirmModal from '../components/common/ConfirmModal';
import { getApiBaseUrl } from '../utils/getApiUrl';

interface Service {
  id: number;
  name: string;
  description?: string;
  price?: number;
  is_active: boolean;
  created_at: string;
  updated_at?: string;
}

const AdminServices: React.FC = () => {
  const navigate = useNavigate();
  const [services, setServices] = useState<Service[]>([]);
  const [filteredServices, setFilteredServices] = useState<Service[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'info' | 'warning' | 'error' } | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingService, setEditingService] = useState<Service | null>(null);
  const [newServiceName, setNewServiceName] = useState('');
  const [editServiceName, setEditServiceName] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showForceDeleteConfirm, setShowForceDeleteConfirm] = useState(false);
  const [serviceToDelete, setServiceToDelete] = useState<number | null>(null);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    const role = localStorage.getItem('admin_role') || 'user';

    if (!token) {
      navigate('/admin/login');
      return;
    }

    if (role !== 'admin') {
      navigate('/admin/dashboard');
      return;
    }

    fetchServices();
  }, [navigate]);

  useEffect(() => {
    const filtered = services.filter(service =>
      service.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
    setFilteredServices(filtered);
  }, [searchQuery, services]);

  const fetchServices = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const apiBaseUrl = getApiBaseUrl();
      const response = await fetch(`${apiBaseUrl}/admin/services`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('خطا در دریافت لیست خدمات');
      }

      const data = await response.json();
      setServices(data);
      setFilteredServices(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddService = async () => {
    if (!newServiceName.trim()) {
      setNotification({ message: 'لطفاً نام خدمت را وارد کنید', type: 'warning' });
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      const apiBaseUrl = getApiBaseUrl();
      const response = await fetch(`${apiBaseUrl}/admin/services`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: newServiceName.trim(),
          is_active: true
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'خطا در ایجاد خدمت');
      }

      setNewServiceName('');
      setShowAddModal(false);
      fetchServices();
      setNotification({ message: 'خدمت با موفقیت اضافه شد', type: 'success' });
    } catch (err: any) {
      setError(err.message);
      setNotification({ message: err.message || 'خطا در ایجاد خدمت', type: 'error' });
    }
  };

  const handleEditService = async () => {
    if (!editServiceName.trim() || !editingService) {
      setNotification({ message: 'لطفاً نام خدمت را وارد کنید', type: 'warning' });
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      const apiBaseUrl = getApiBaseUrl();
      const response = await fetch(`${apiBaseUrl}/admin/services/${editingService.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: editServiceName.trim()
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'خطا در به‌روزرسانی خدمت');
      }

      setEditServiceName('');
      setEditingService(null);
      setShowEditModal(false);
      fetchServices();
      setNotification({ message: 'خدمت با موفقیت به‌روزرسانی شد', type: 'success' });
    } catch (err: any) {
      setError(err.message);
      setNotification({ message: err.message || 'خطا در به‌روزرسانی خدمت', type: 'error' });
    }
  };

  const handleDeleteService = (serviceId: number) => {
    setServiceToDelete(serviceId);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteService = async () => {
    if (!serviceToDelete) return;

    try {
      const token = localStorage.getItem('admin_token');
      const apiBaseUrl = getApiBaseUrl();

      let response = await fetch(`${apiBaseUrl}/admin/services/${serviceToDelete}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();

        if (errorData.detail && errorData.detail.includes('نوبت فعال')) {
          setShowDeleteConfirm(false);
          setShowForceDeleteConfirm(true);
          return;
        }

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || 'خطا در حذف خدمت');
        }
      }

      setShowDeleteConfirm(false);
      setServiceToDelete(null);
      fetchServices();
      setNotification({ message: 'خدمت با موفقیت حذف شد', type: 'success' });
    } catch (err: any) {
      setError(err.message);
      setNotification({ message: err.message || 'خطا در حذف خدمت', type: 'error' });
      setShowDeleteConfirm(false);
      setServiceToDelete(null);
    }
  };

  const confirmForceDeleteService = async () => {
    if (!serviceToDelete) return;

    try {
      const token = localStorage.getItem('admin_token');
      const apiBaseUrl = getApiBaseUrl();

      const response = await fetch(`${apiBaseUrl}/admin/services/${serviceToDelete}/force`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'خطا در حذف خدمت');
      }

      setShowForceDeleteConfirm(false);
      setServiceToDelete(null);
      fetchServices();
      setNotification({ message: 'خدمت با موفقیت حذف شد', type: 'success' });
    } catch (err: any) {
      setError(err.message);
      setNotification({ message: err.message || 'خطا در حذف خدمت', type: 'error' });
      setShowForceDeleteConfirm(false);
      setServiceToDelete(null);
    }
  };

  const openEditModal = (service: Service) => {
    setEditingService(service);
    setEditServiceName(service.name);
    setShowEditModal(true);
  };

  const closeAddModal = () => {
    setShowAddModal(false);
    setNewServiceName('');
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setEditingService(null);
    setEditServiceName('');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8">
        {notification && (
          <Notification
            message={notification.message}
            type={notification.type}
            onClose={() => setNotification(null)}
          />
        )}
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">خطا</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{error}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}
      <ConfirmModal
        isOpen={showDeleteConfirm}
        title="حذف خدمت"
        message="آیا از حذف این خدمت اطمینان دارید؟"
        onConfirm={confirmDeleteService}
        onCancel={() => {
          setShowDeleteConfirm(false);
          setServiceToDelete(null);
        }}
        confirmText="حذف"
        cancelText="لغو"
      />
      <ConfirmModal
        isOpen={showForceDeleteConfirm}
        title="حذف اجباری خدمت"
        message="این خدمت دارای نوبت‌های فعال است. آیا می‌خواهید به صورت اجباری حذف شود؟"
        onConfirm={confirmForceDeleteService}
        onCancel={() => {
          setShowForceDeleteConfirm(false);
          setServiceToDelete(null);
        }}
        confirmText="حذف اجباری"
        cancelText="لغو"
      />

      {/* Header & Actions */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">مدیریت خدمات</h1>
          <p className="mt-1 text-sm text-gray-500">لیست خدمات قابل ارائه در سیستم</p>
        </div>
        <div className="flex gap-3">
          <div className="relative">
            <input
              type="text"
              placeholder="جستجوی خدمت..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-full md:w-64"
            />
            <svg
              className="absolute left-3 top-2.5 h-5 w-5 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <span className="hidden sm:inline">افزودن خدمت</span>
          </button>
        </div>
      </div>

      {/* Services Grid */}
      {filteredServices.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-12 text-center border border-gray-100">
          <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-1">هیچ خدمتی یافت نشد</h3>
          <p className="text-gray-500">
            {searchQuery ? 'نتیجه‌ای برای جستجوی شما پیدا نشد.' : 'هنوز خدمتی تعریف نشده است.'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredServices.map((service) => (
            <div key={service.id} className="bg-white rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-200 overflow-hidden group">
              <div className="p-5">
                <div className="flex justify-between items-start mb-4">
                  <div className="p-2 bg-blue-50 rounded-lg text-blue-600">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${service.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                    {service.is_active ? 'فعال' : 'غیرفعال'}
                  </span>
                </div>

                <h3 className="text-lg font-bold text-gray-900 mb-2 truncate" title={service.name}>
                  {service.name}
                </h3>

                <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-50">
                  <div className="flex gap-2 w-full">
                    <button
                      onClick={() => openEditModal(service)}
                      className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                      ویرایش
                    </button>
                    <button
                      onClick={() => handleDeleteService(service.id)}
                      className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-sm font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      حذف
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* مودال افزودن خدمت */}
      {showAddModal && (
        <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex justify-center items-center">
          <div className="relative p-6 w-full max-w-md bg-white rounded-2xl shadow-xl mx-4 transform transition-all">
            <div className="mb-6">
              <h3 className="text-xl font-bold text-gray-900 text-center">
                افزودن خدمت جدید
              </h3>
              <p className="text-sm text-gray-500 text-center mt-1">اطلاعات خدمت جدید را وارد کنید</p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  نام خدمت <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newServiceName}
                  onChange={(e) => setNewServiceName(e.target.value)}
                  placeholder="مثلاً: لیزر موهای زائد"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  autoFocus
                />
              </div>

              <div className="flex gap-3 mt-8">
                <button
                  onClick={closeAddModal}
                  className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors"
                >
                  انصراف
                </button>
                <button
                  onClick={handleAddService}
                  className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={!newServiceName.trim()}
                >
                  افزودن خدمت
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* مودال ویرایش خدمت */}
      {showEditModal && editingService && (
        <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex justify-center items-center">
          <div className="relative p-6 w-full max-w-md bg-white rounded-2xl shadow-xl mx-4 transform transition-all">
            <div className="mb-6">
              <h3 className="text-xl font-bold text-gray-900 text-center">
                ویرایش خدمت
              </h3>
              <p className="text-sm text-gray-500 text-center mt-1">تغییر نام خدمت "{editingService.name}"</p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  نام خدمت <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={editServiceName}
                  onChange={(e) => setEditServiceName(e.target.value)}
                  placeholder="نام خدمت را وارد کنید"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  autoFocus
                />
              </div>

              <div className="flex gap-3 mt-8">
                <button
                  onClick={closeEditModal}
                  className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors"
                >
                  انصراف
                </button>
                <button
                  onClick={handleEditService}
                  className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={!editServiceName.trim()}
                >
                  ذخیره تغییرات
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminServices;
